import React, { useRef, useState } from "react";
import { Upload, Check, X, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * Real file uploader that POSTs to /api/uploads and reports {file_id, original_filename}.
 * Value shape: { file_id, filename } or null.
 */
export const FileUpload = ({ value, onChange, purpose = "generic", accept = ".pdf,.png,.jpg,.jpeg,.webp", label, testId, hint }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const pick = () => ref.current?.click();

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads?purpose=${encodeURIComponent(purpose)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ file_id: data.file_id, filename: data.original_filename });
      toast.success("Uploaded", { description: data.original_filename });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const clear = (e) => { e.stopPropagation(); onChange(null); };

  return (
    <div>
      {label && <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">{label}</div>}
      <button
        type="button"
        onClick={pick}
        data-testid={testId}
        className="w-full rounded-md border p-4 flex items-center gap-4 text-left transition-colors duration-200 hover:border-[#3A3A42]"
        style={{ borderColor: value ? "rgba(16,185,129,0.35)" : "#1E1E22", background: "var(--surface)" }}
      >
        <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
             style={{
               background: value ? "rgba(16,185,129,0.10)" : "rgba(59,130,246,0.08)",
               border: `1px solid ${value ? "rgba(16,185,129,0.35)" : "rgba(59,130,246,0.25)"}`,
             }}>
          {busy ? <Loader2 size={13} className="text-blue-400 animate-spin"/> :
           value ? <Check size={14} className="text-emerald-400"/> :
                   <Upload size={13} className="text-blue-400"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">
            {busy ? "Uploading…" : (value?.filename || (hint || "Click to upload"))}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">PDF, JPG, PNG · max 10 MB</div>
        </div>
        {value && !busy && (
          <span onClick={clear} className="text-zinc-500 hover:text-rose-400 p-1 transition-colors duration-200">
            <X size={13}/>
          </span>
        )}
      </button>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={e => upload(e.target.files?.[0])}/>
    </div>
  );
};

export default FileUpload;
